import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFunnelMapApiFacade } from '../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import useCrmOrgPermissions from '../../../common/permissions/useCrmOrgPermissions';
import { simpleHash } from '../../../../third-party/simpleHash';
import { telemetry } from '../../../../telemetry';
import isEqual from 'lodash/isEqual';
import { useAutoSaveContext } from './AutoSaveContext';
import { FunnelMapSaveData } from './useAutoSave';
import { funnelMapToFunnelMapPutPayload } from '../../../../apis/facades/funnel-map/funnelMapToFunnelMapPutPayload';
import { isAxiosError } from 'axios';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';

const FUNNEL_MAP = 'FUNNEL_MAP';

interface UseFunnelMapAutoSave {
  funnelMap: FunnelMap;
  setLastUpdatedFor: (object: string, date: string) => void;
}

const extractFunnelMapSaveData = (
  funnelMap: Pick<
    FunnelMap,
    | 'id'
    | 'funnels'
    | 'name'
    | 'thirdPartyFunnels'
    | 'metadataElements'
    | 'sections'
    | 'stickyNotes'
    | 'texts'
  >,
): FunnelMapSaveData => {
  const { id, funnels, name, thirdPartyFunnels, metadataElements, sections, stickyNotes, texts } =
    funnelMap;
  return {
    id,
    funnels,
    name,
    thirdPartyFunnels,
    metadataElements,
    sections,
    stickyNotes,
    texts,
  };
};

const funnelMapSaveDataToHash = (funnelMapSaveData: FunnelMapSaveData) => {
  return simpleHash(JSON.stringify(funnelMapSaveData));
};

export const useFunnelMapAutoSave = ({ funnelMap, setLastUpdatedFor }: UseFunnelMapAutoSave) => {
  // Hooks - all declared at the top
  const { addNotification } = useSweepNotifications();
  const {
    getErrorHashFor,
    removeError,
    setErrorsFor,
    setFunnelEntityIsDirty,
    setIsSavingFor,
    funnelMapVersion,
  } = useAutoSaveContext();
  const [hasEditFunnelPermissions] = useCrmOrgPermissions(['edit:funnels']);
  const { put_funnelMap } = useFunnelMapApiFacade();

  const funnelMapSaveData: FunnelMapSaveData = useMemo(
    () => extractFunnelMapSaveData(funnelMap),
    [funnelMap],
  );

  // Refs for managing save state
  const queuedFunnelMapData = useRef<FunnelMapSaveData | null>(null);
  const lastSavedFunnelMapSaveData = useRef<FunnelMapSaveData | null>(funnelMapSaveData);
  const currentSavingFunnelMapSaveData = useRef<FunnelMapSaveData | null>(null);

  const saveFunnelMap = useCallback(
    async (funnelMapId: string, funnelMapSaveData: FunnelMapSaveData, version: number) => {
      try {
        setFunnelEntityIsDirty(FUNNEL_MAP, true);
        setIsSavingFor(FUNNEL_MAP, true);

        const funnelMapPayload = funnelMapToFunnelMapPutPayload(funnelMapSaveData, version);
        currentSavingFunnelMapSaveData.current = funnelMapSaveData;
        const updatedFunnelMap = await put_funnelMap(funnelMapId, funnelMapPayload);

        setLastUpdatedFor(FUNNEL_MAP, updatedFunnelMap.updatedAt);
        setFunnelEntityIsDirty(FUNNEL_MAP, false);
        removeError(FUNNEL_MAP);

        // Update the version from the server response to prevent optimistic lock errors
        funnelMapVersion.current = updatedFunnelMap.version;
        currentSavingFunnelMapSaveData.current = null;

        // Process queued saves
        if (queuedFunnelMapData.current) {
          const nextData = queuedFunnelMapData.current;
          queuedFunnelMapData.current = null;
          await saveFunnelMap(funnelMapId, nextData, updatedFunnelMap.version);
        } else {
          // Only update lastSaved when no more queued saves
          lastSavedFunnelMapSaveData.current = funnelMapSaveData;
        }
      } catch (err) {
        currentSavingFunnelMapSaveData.current = null;
        queuedFunnelMapData.current = null;

        const funnelMapSaveDataHash = simpleHash(JSON.stringify(funnelMapSaveData));
        setErrorsFor(FUNNEL_MAP, funnelMapSaveDataHash);

        telemetry.captureError(err, {
          context: 'useFunnelMapAutoSave',
          funnelMapId: funnelMapSaveData.id,
          version,
          statusCode: isAxiosError(err) ? err.response?.status : undefined,
        });

        const is409Conflict = isAxiosError(err) && err.response?.status === 409;
        const message = is409Conflict
          ? 'Could not save funnel. This funnel has been updated outside of this tab. Please refresh the page and try again.'
          : 'Could not save funnel. Please try again.';

        addNotification({
          message,
          variant: SweepNotificationVariant.Error,
          keepOpen: true,
        });
      } finally {
        setIsSavingFor(FUNNEL_MAP, false);
      }
    },
    [
      addNotification,
      funnelMapVersion,
      put_funnelMap,
      removeError,
      setErrorsFor,
      setFunnelEntityIsDirty,
      setIsSavingFor,
      setLastUpdatedFor,
    ],
  );

  useEffect(() => {
    funnelMapVersion.current = funnelMap.version;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasEditFunnelPermissions) {
      return;
    }
    const isSavingFunnelMap = currentSavingFunnelMapSaveData.current !== null;
    const funnelMapHasChanges = !isEqual(
      currentSavingFunnelMapSaveData.current || lastSavedFunnelMapSaveData.current,
      funnelMapSaveData,
    );
    const funnelMapSavingError =
      getErrorHashFor(FUNNEL_MAP) === funnelMapSaveDataToHash(funnelMapSaveData);

    if (!funnelMapSavingError && funnelMapHasChanges) {
      if (isSavingFunnelMap) {
        queuedFunnelMapData.current = funnelMapSaveData;
      } else {
        saveFunnelMap(funnelMapSaveData.id, funnelMapSaveData, funnelMapVersion.current);
      }
    }
  }, [
    funnelMapVersion,
    funnelMapSaveData,
    getErrorHashFor,
    hasEditFunnelPermissions,
    saveFunnelMap,
  ]);
};
